import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgbActiveModal, NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { User } from 'src/app/classes/User';
import { Subscription } from 'src/app/classes/Subscription';
import { PaymentMethods } from 'src/app/enums/PaymentMethods';
import { SubscriptionType } from 'src/app/enums/SubscriptionType';

export interface UserAddSubscriptionModalResult {
  user: User;
  subscription: Subscription | null;
  dateStart?: string;
  paymentMethod: string;
  amount: number | null;
  taxRequired: boolean;
  childId?: number;
  bundleIds?: number[];
}

@Component({
  selector: 'app-user-edit',
  styleUrls: ['./user-add-subscription.component.scss'],
  templateUrl: './user-add-subscription.component.html',
  encapsulation: ViewEncapsulation.None
})
export class UserAddSubscriptionComponent implements OnInit, OnChanges {

  @Input() user: User;
  @Input() subscriptions: Subscription[];
  @ViewChild('datepicker', { static: false }) public datepicker: NgbInputDatepicker;

  public PaymentMethods = PaymentMethods;

  public selectedSubscription: Subscription | null = null;
  public selectedChildId: number | null = null;
  public selectedCampBundleId: number | null = null;
  public dateStart: NgbDateStruct;
  public paymentMethods: string[] = Object.values(Object.fromEntries(
    Object.entries(this.PaymentMethods).filter(([key]) => key !== 'STRIPE')));
  public paymentMethod: string = this.PaymentMethods.CARD;

  public amount: number | null = null;
  public taxRequired = false;
  public errorMessage: string | null = null;

  constructor(
    private activeModal: NgbActiveModal) {
    const today = new Date();
    this.dateStart = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate()
    };
  }

  ngOnInit(): void {
    this.normalizeChildrenAndPreselectSingleChild();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['user']) {
      this.normalizeChildrenAndPreselectSingleChild();
    }
  }

  closeModal(reason?: string): void {
    let data: UserAddSubscriptionModalResult | undefined;
    if (reason !== 'close') {
      if (this.paymentMethod === PaymentMethods.LINK && (!this.amount || this.amount <= 0)) {
        this.errorMessage = 'Enter a valid payment amount.';
        return;
      }
      if (this.paymentMethod !== PaymentMethods.LINK && !this.selectedSubscription) {
        this.errorMessage = 'Select a subscription before saving.';
        return;
      }
      if (this.isChildRequired() && !this.selectedChildId) {
        this.preselectSingleChildIfPossible();
      }
      if (this.isChildRequired() && !this.selectedChildId) {
        this.errorMessage = 'Select or create a child for course or event subscriptions.';
        return;
      }
      if (this.isCampSubscription() && !this.selectedCampBundleId) {
        this.errorMessage = 'Choose camp periods.';
        return;
      }

      const dateStart = this.shouldShowStartDate() ? this.formatDate(this.dateStart) : undefined;
      const bundleIds = this.isCampSubscription() && this.selectedCampBundleId
        ? [this.selectedCampBundleId]
        : undefined;
      data = {
        user: this.user,
        subscription: this.selectedSubscription,
        dateStart: this.formatDate(this.dateStart),
        paymentMethod: this.paymentMethod,
        amount: this.amount,
        taxRequired: this.taxRequired,
        childId: this.selectedChildId ?? undefined,
        bundleIds,
      };
    }
    this.activeModal.close(data);
  }

  protected formatDate(date: NgbDateStruct): string {
    const year = date.year;
    const month = String(date.month).padStart(2, '0');
    const day = String(date.day).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  handlePaymentMethod(event: string): void {
    this.paymentMethod = event;
    this.errorMessage = null;
  }

  public handleSubscriptionChange(): void {
    this.selectedCampBundleId = null;
    this.errorMessage = null;
  }

  public isChildRequired(): boolean {
    if (this.paymentMethod === PaymentMethods.LINK || !this.selectedSubscription) {
      return false;
    }
    const subscriptionType = this.selectedSubscription.subscriptionType;
    if (subscriptionType === SubscriptionType.COURSE || subscriptionType === SubscriptionType.EVENT) {
      return true;
    }
    return !!this.selectedSubscription.bundleId || !!this.selectedSubscription.camp;
  }

  public isCampSubscription(): boolean {
    if (!this.selectedSubscription) {
      return false;
    }
    const subscriptionType = this.selectedSubscription.subscriptionType;
    return subscriptionType === SubscriptionType.EVENT || !!this.selectedSubscription.camp;
  }

  public shouldShowStartDate(): boolean {
    if (this.paymentMethod === PaymentMethods.LINK || !this.selectedSubscription) {
      return false;
    }
    const subscriptionType = this.selectedSubscription.subscriptionType;
    if (subscriptionType !== undefined && subscriptionType !== null) {
      return subscriptionType === SubscriptionType.REGULAR;
    }
    // Fallback for legacy data without explicit subscriptionType.
    return !this.selectedSubscription.bundleId && !this.selectedSubscription.camp;
  }

  public getCampPeriods(): Array<{ bundleId: number; periodStartDate: string; periodEndDate: string }> {
    return this.selectedSubscription?.camp?.periods ?? [];
  }

  private normalizeChildrenAndPreselectSingleChild(): void {
    if (!this.user) {
      return;
    }
    this.user.children = this.user.children ?? [];
    this.preselectSingleChildIfPossible();
  }

  private preselectSingleChildIfPossible(): void {
    if (this.selectedChildId || !this.user?.children?.length) {
      return;
    }
    if (this.user.children.length === 1) {
      this.selectedChildId = this.user.children[0].id;
    }
  }
}
